/**
 * Phase BACKGROUND-ANALYSIS-P1a.5-vi — strip-mount slot (presentational).
 *
 * Receives polled `meta` from parent (single source of truth); no longer
 * instantiates useOutfitMetaPolling itself — prevents double-polling when
 * the parent (Results host) also needs meta for the hero-variant spinner
 * gate. `expectsLb3` is owned by parent because it depends on auth +
 * unfiltered wardrobe_items count (mirrors orchestrator at
 * analyse-outfit/index.ts:2890).
 *
 * Strip status derivation:
 *   - analysis_status === 'complete' AND all expected LB columns non-null
 *     → unmount (returns null)
 *   - analysis_status === 'failed' OR lookbook_status === 'failed'
 *     → 'failed_analysis'
 *   - isStuck (after 250ms guard) → 'failed_analysis'
 *   - otherwise → 'analysing'
 *
 * On successful retry dispatch, calls onRetryDispatched(newOutfitId).
 */
import { useEffect, useState } from "react";
import PendingStatusStrip from "@/components/results/PendingStatusStrip";
import type { OutfitMeta } from "@/hooks/useOutfitMetaPolling";
import { useStuckDetection } from "@/hooks/useStuckDetection";
import { useRetryAnalysis } from "@/hooks/useRetryAnalysis";

interface Props {
  outfitId: string | null;
  meta: OutfitMeta | null;
  expectsLb3: boolean;
  onRetryDispatched?: (newOutfitId: string) => void;
}

export default function PendingStripSlot({
  outfitId,
  meta,
  expectsLb3,
  onRetryDispatched,
}: Props) {
  const isStuck = useStuckDetection(meta);
  const { retry, canRetry, status: retryStatus } = useRetryAnalysis(outfitId);

  // 250ms minimum-visible guard on stuck → failed flip
  const [showStuckCTA, setShowStuckCTA] = useState(false);
  useEffect(() => {
    if (!isStuck) {
      setShowStuckCTA(false);
      return;
    }
    const t = window.setTimeout(() => setShowStuckCTA(true), 250);
    return () => window.clearTimeout(t);
  }, [isStuck]);

  if (!meta) return null;

  const aStatus = meta.analysis_status;
  const lStatus = meta.lookbook_status;

  // Terminal-success → unmount (column-based; mirrors useOutfitMetaPolling)
  const lb1 = !!meta.lookbook_image_url;
  const lb2 = !!meta.lookbook_lb2_url;
  const lb3 = !expectsLb3 || !!meta.lookbook_lb3_url;
  if (aStatus === "complete" && lb1 && lb2 && lb3) return null;

  const failed = aStatus === "failed" || lStatus === "failed";
  const stripStatus: "analysing" | "failed_analysis" =
    failed || showStuckCTA ? "failed_analysis" : "analysing";

  const onRetry =
    stripStatus === "failed_analysis" && canRetry
      ? async () => {
          const trigger = showStuckCTA ? "stuck" : "failed";
          const res = await retry(trigger);
          if (res?.newOutfitId && onRetryDispatched) {
            onRetryDispatched(res.newOutfitId);
          }
        }
      : undefined;

  return (
    <PendingStatusStrip
      status={stripStatus}
      outfitId={meta.id}
      canRetry={canRetry && retryStatus !== "retrying"}
      onRetry={onRetry}
    />
  );
}
